import {
  EMAIL_DOCUMENT_ALLOWED_URL_SCHEMES,
  EMAIL_DOCUMENT_MAX_URL_LENGTH,
} from "./emailDocumentContract";
import { isMergeTagKey } from "../domain/merge-tags";

const MERGE_TAG_PATTERN = /{{\s*([\w.]+)\s*}}/g;

export type TEmailDocumentLinkUrlClassification = "ok" | "missing" | "invalid";

export function normalizeRestrictedRichTextLinkHref(href: string): string {
  const trimmed = href.trim();
  if (trimmed === "") {
    return "";
  }

  if (/^(https?:|mailto:|tel:|\{\{)/i.test(trimmed)) {
    return trimmed;
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return `mailto:${trimmed}`;
  }

  return `https://${trimmed}`;
}

export function isEmailDocumentHttpHostnameValid(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  if (host === "") {
    return false;
  }

  if (host === "localhost") {
    return true;
  }

  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(host)) {
    return true;
  }

  if (!host.includes(".")) {
    return false;
  }

  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(host);
}

export function normalizeEmailBuilderSocialLinkUrlFieldValue(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return "";
  }

  if (/^https?:/i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function classifyEmailDocumentSocialLinkUrl(rawUrl: string): TEmailDocumentLinkUrlClassification {
  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return "missing";
  }

  if (trimmed.length > EMAIL_DOCUMENT_MAX_URL_LENGTH) {
    return "invalid";
  }

  if (!/^\s*(https?:)/i.test(trimmed)) {
    return "invalid";
  }

  try {
    const parsed = new URL(trimmed);
    const scheme = parsed.protocol.replace(/:$/, "").toLowerCase();
    if (scheme !== "http" && scheme !== "https") {
      return "invalid";
    }

    if (parsed.hostname === "" || !isEmailDocumentHttpHostnameValid(parsed.hostname)) {
      return "invalid";
    }

    return "ok";
  } catch {
    return "invalid";
  }
}

export function isEmailBuilderSocialLinkUrlFieldValueValid(rawUrl: string): boolean {
  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return true;
  }

  return classifyEmailDocumentSocialLinkUrl(normalizeEmailBuilderSocialLinkUrlFieldValue(trimmed)) === "ok";
}

export function classifyEmailDocumentLinkUrl(rawUrl: string): TEmailDocumentLinkUrlClassification {
  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return "missing";
  }

  if (trimmed.length > EMAIL_DOCUMENT_MAX_URL_LENGTH) {
    return "invalid";
  }

  const mergeTagPattern = new RegExp(MERGE_TAG_PATTERN.source, "g");
  let mergeMatch: RegExpExecArray | null = mergeTagPattern.exec(trimmed);
  while (mergeMatch !== null) {
    const tag = mergeMatch[1];
    if (!isMergeTagKey(tag)) {
      return "invalid";
    }
    mergeMatch = mergeTagPattern.exec(trimmed);
  }

  if (/^\s*{{/i.test(trimmed)) {
    return "ok";
  }

  if (!/^\s*(https?:|mailto:|tel:)/i.test(trimmed)) {
    return "invalid";
  }

  try {
    const parsed = new URL(trimmed);
    const scheme = parsed.protocol.replace(/:$/, "").toLowerCase();
    if (!(EMAIL_DOCUMENT_ALLOWED_URL_SCHEMES as readonly string[]).includes(scheme)) {
      return "invalid";
    }

    if ((scheme === "http" || scheme === "https") && parsed.hostname === "") {
      return "invalid";
    }

    if ((scheme === "http" || scheme === "https") && !isEmailDocumentHttpHostnameValid(parsed.hostname)) {
      return "invalid";
    }

    return "ok";
  } catch {
    return "invalid";
  }
}

export function resolveEmailDocumentOptionalLinkUrl(
  rawUrl: unknown,
  emptyValue: "" | null,
): string | null {
  if (rawUrl === undefined || rawUrl === null) {
    return emptyValue;
  }

  if (typeof rawUrl !== "string") {
    return emptyValue;
  }

  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return emptyValue;
  }

  const normalized = normalizeRestrictedRichTextLinkHref(trimmed);
  if (classifyEmailDocumentLinkUrl(normalized) !== "ok") {
    return emptyValue;
  }

  return normalized;
}
